import ModelViewer from "components/ModelViewer";
import RenderTurntable from "components/RenderTurntable/RenderTurntable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ModelViewerContainer.scss";
import { useSelector } from "react-redux";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { isShoeProduct } from "utils/productType";
import {
  CAMERA_MODE as CAMERA_MODE_DEFAULT,
  CAMERA_TYPE,
} from "configs/RendererConfig";
import Button from "components/Button";
import { TextField } from "@mui/material";
import {
  isJockey1842Inline,
  isJockey1820Inline,
  applyJockey1842Overrides,
} from "utils/jockeyInlineCamera";
import { IFRAME_ACTIONS } from "actions/IframeActions";

// 1820 (enamor id 210) render-turntable swap: the resolved avatar body GLB is
// female_avatar_enamor210/<cyrb53 hash>.glb. When that hash matches one of the
// 24 pre-rendered bodies, we show the drag-spin render set in place of the live
// 3D ModelViewer. Everything else (other products/sizes) keeps the 3D viewer.
const HASH_TO_BODY = {
  "2026241588004564": "Round_B34", "2131267163785039": "Round_C34", "7526287670059094": "Round_D34",
  "7940203885221927": "Round_B40", "4299820371347656": "Round_C40", "1480246470336261": "Round_D40",
  "5894581710582871": "Teardrop_B34", "4475621685407394": "Teardrop_C34", "4079809039548507": "Teardrop_D34",
  "1521515935327393": "Teardrop_B40", "8676933775311892": "Teardrop_C40", "4109509046733551": "Teardrop_D40",
  "5532335007368875": "Bell_B34", "8996010321179786": "Bell_C34", "7523642356444662": "Bell_D34",
  "1177614024625517": "Bell_B40", "3188512714913818": "Bell_C40", "4243938307866884": "Bell_D40",
  "8523955512527312": "EastWest_B34", "7605052208747016": "EastWest_C34", "8312298808902586": "EastWest_D34",
  "3916060397560495": "EastWest_B40", "947236130792672": "EastWest_C40", "3834343145538386": "EastWest_D40",
};
// 301 (Jockey 1722) is a 3D product (Genesis-9 avatars, enamor301) mapped onto the
// turntable pipeline. Unlike 1820/MI it has its OWN body grid: bases are 36 and 40
// (the 40 asset is the 42-base body baked down x/y). These are the live
// female_avatar_enamor301/<hash>.glb bodyIdentifiers (getAvatarUrl); each has a
// separately-fitted 301A bra baked into its render. Shape here = Round/Teardrop/Bell
// (apple/pear/lemon regular). Bands 30/32/34 x-scale off 36, 38 off 40.
const HASH_TO_BODY_301 = {
  "6610557077685204": "Round_B36", "8688139304847202": "Round_C36", "8693162303545606": "Round_D36",
  "4032131361023021": "Round_B40", "1043905232000525": "Round_C40", "6850880692768486": "Round_D40",
  "8053084036192419": "Teardrop_B36", "1228527893720898": "Teardrop_C36", "1272578051601827": "Teardrop_D36",
  "2057952273264215": "Teardrop_B40", "6905257057977171": "Teardrop_C40", "3544165365025089": "Teardrop_D40",
  "1047423917361139": "Bell_B36", "3687258406864230": "Bell_C36", "6048493796076989": "Bell_D36",
  "3114686702797534": "Bell_B40", "4900311462889873": "Bell_C40", "6334803284423101": "Bell_D40",
};
// Map the avatar product id → turntable frame-set prefix. The body grid
// (Shape_CupBand) is shared across products; only the bra (and thus the
// rendered/upscaled frames) differ. 210 = 1820 Fragrant Lily, 216 = MI16 Grape
// Wine. Returns { body, product } or null when it's not a turntable product.
const PRODUCT_PREFIX = { enamor210: "1820", enamor216: "MI16", enamor217: "MI10", enamor213: "JW11", enamor212: "1841", enamor218: "FE93", enamor215: "1816", enamor301: "301" };
const renderBodyFromAvatar = (avatarAssets) => {
  const url = (avatarAssets && avatarAssets.avatarUrl) || "";
  const pm = url.match(/enamor\d+/);
  const product = pm && PRODUCT_PREFIX[pm[0]];
  if (!product) return null;
  const m = url.match(/\/(\d+)\.glb/);
  // 301 has its own body grid (36/40 bases); everything else shares HASH_TO_BODY.
  const table = product === "301" ? HASH_TO_BODY_301 : HASH_TO_BODY;
  const body = m && table[m[1]];
  return body ? { body, product } : null;
};

const mapStateToProps = ({ avatarReducer = {} }) => ({
  product: get(avatarReducer, "product", {}),
  avatarAssets: get(avatarReducer, "avatarAssets", null),
  userProfile: get(avatarReducer, "userProfile", {}),
  isDefaultAvatar: get(avatarReducer, "isDefaultAvatar", false),
  defaultUserProfile: get(avatarReducer, "defaultUserProfile"),
  lightTuning: get(avatarReducer, "lightTuning", null),
  sizeRecommendations: get(avatarReducer, "sizeRecommendations", {}),
  turntableSizeSel: get(avatarReducer, "turntableSize", null),
  turntableShapeSel: get(avatarReducer, "turntableShape", null),
});

const getCameraPosition = (cameraMode) => {
  return {
    cameraTarget: cameraMode.modelTarget,
    cameraOrbit: cameraMode.modelOrbit,
    fieldOfView: cameraMode.modelFov,
    maxCameraOrbit: cameraMode.maxCameraOrbit,
    minCameraOrbit: cameraMode.minCameraOrbit,
  };
};

// Jockey-1722/1842/216 inline experiences override the default IBL.
// modelViewerList[0] (pillars_1k.hdr) is a sunset-lit pillars scene with
// a warm horizon — visibly tints the lit avatar warm. Force a white HDRI
// for inline routes so the avatar reads as neutrally lit.
const JOCKEY_INLINE_ENV = "/environment/HDRI_White_02A.hdr";
const JOCKEY_1820_HDR = "/environment/pillars_1k.hdr";

export const getDefaultEnvironment = (selectedProduct, selectedSkybox) => {
  // 1820 inline matches the POC picker (pillars HDR + skybox).
  if (isJockey1820Inline()) return JOCKEY_1820_HDR;
  if (isJockey1842Inline()) return JOCKEY_INLINE_ENV;
  return !isEmpty(selectedSkybox)
    ? selectedSkybox.environment
    : isShoeProduct(selectedProduct)
      ? ""
      : "/environment/pillars_1k.hdr";
};

export const getDefaultSkybox = (selectedProduct, selectedSkybox) => {
  // 1820 inline shows the pillars skybox behind the avatar (POC-picker
  // parity). Other inline routes hide the skybox so the white stage CSS
  // background shows through — the IBL alone lights the avatar.
  if (isJockey1820Inline()) return JOCKEY_1820_HDR;
  if (isJockey1842Inline()) return "";
  return !isEmpty(selectedSkybox)
    ? selectedSkybox.skybox
    : selectedProduct.brand !== "revolve" ||
      selectedProduct.brand !== "freepeople"
      ? ""
      : "/environment/pillars_1k.hdr";
};

const ModelViewerContainer = ({
  showHotspots,
  selectedProduct,
  cameraMode,
  setCameraMode,
  setSceneLoading,
  sceneLoading,
  selectedSkybox,
  braSizeLabel,
}) => {
  const {
    avatarAssets = null,
    userProfile = {},
    defaultUserProfile = {},
    isDefaultAvatar,
    lightTuning,
    sizeRecommendations = {},
    turntableSizeSel = null,
    turntableShapeSel = null,
  } = useSelector(mapStateToProps);

  const [selectedScene, setSelectedScene] = useState(null);
  const [isScalingActive, setIsScalingActive] = useState(false);

  // Fixed-viewport ("panel") mode: the host (e.g. the drawer/sheet panel) opens
  // the experience in a box of fixed size. In this mode we DON'T switch to flow
  // layout — we keep the original fixed-viewport layout where the turntable sits
  // in a fixed top slice and the cards are pinned to the bottom, overlapping the
  // turntable when a card is tall (instead of pushing it / resizing the frame).
  const fixedViewport = useMemo(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("fixedViewport") === "1",
    []
  );

  // Card text size, dialled from OUTSIDE the build. The host passes
  // ?textScale=110 (percent) — on Shopify that's a theme-editor setting on the
  // Dopplr block, so the type can be tuned live with the brand instead of
  // waiting on a plugin build. Applied as a multiplier on the card's type via
  // --jk-text-scale; clamped so a typo can't make the panel unusable.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const raw = new URLSearchParams(window.location.search).get("textScale");
    const pct = Number(raw);
    if (!raw || !isFinite(pct)) return undefined;
    const scale = Math.max(0.7, Math.min(pct / 100, 1.5));
    document.body.style.setProperty("--jk-text-scale", String(scale));
    return () => document.body.style.removeProperty("--jk-text-scale");
  }, []);

  // When the render turntable is the active view, keep the BottomMenu cards
  // un-gated. The turntable is image-based (no scene to load), but the
  // experience flips sceneLoading=true on body/size changes and the <img>
  // onLoad misses cached frames — which left the cards blank on first load
  // until the user interacted. Clearing it here whenever the turntable is
  // active (and sceneLoading is stuck true) keeps the cards always present.
  const activeRenderBody = renderBodyFromAvatar(avatarAssets);
  // Stable string key for effect deps (activeRenderBody is a fresh object each render).
  const activeBodyKey = activeRenderBody
    ? `${activeRenderBody.product}_${activeRenderBody.body}`
    : null;
  useEffect(() => {
    if (activeBodyKey && sceneLoading) setSceneLoading(false);
  }, [activeBodyKey, sceneLoading, setSceneLoading]);

  // Flow mode: when the turntable is active, switch the experience from the
  // fixed-viewport absolute layout to document flow (turntable block on top,
  // cards below) so the plugin height is dynamic. Scoped via this body class so
  // other (model-viewer / 3D) experiences keep the absolute canvas. We also
  // continuously report the document's content height to the parent page
  // (DopplrScript) so the host iframe wrapper grows/shrinks with the cards —
  // the plugin's total length tracks its content instead of a fixed aspect.
  useEffect(() => {
    // In fixed-viewport (panel) mode we stay on the absolute layout — no flow, no
    // height reporting — so the frame size is constant and cards overlap instead.
    if (!activeBodyKey || fixedViewport) return undefined;
    document.body.classList.add("dopplr-flow");
    document.documentElement.classList.add("dopplr-flow");

    let last = 0;
    const postHeight = () => {
      // Use offsetHeight (the rendered content box), NOT scrollHeight. Both
      // documentElement.scrollHeight AND body.scrollHeight are floored to
      // clientHeight (the iframe viewport = the height we last set), so they can
      // never report LESS than the current iframe height — a high-water-mark
      // lock that strands pink padding below the cards. body is content-sized
      // (height:auto in flow), so its offsetHeight is the true bottom and shrinks
      // back when a tab's content gets shorter.
      const h = Math.ceil(document.body.offsetHeight);
      if (!h || Math.abs(h - last) < 2) return;
      last = h;
      window.parent.postMessage(
        { message: IFRAME_ACTIONS.IFRAME_HEIGHT, value: { height: h } },
        "*"
      );
    };
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(postHeight)
        : null;
    if (ro) {
      ro.observe(document.documentElement);
      ro.observe(document.body);
    }
    window.addEventListener("resize", postHeight);
    postHeight();

    return () => {
      document.body.classList.remove("dopplr-flow");
      document.documentElement.classList.remove("dopplr-flow");
      if (ro) ro.disconnect();
      window.removeEventListener("resize", postHeight);
      // Reset the host wrapper back to its aspect-ratio layout for non-flow
      // experiences that may mount next.
      window.parent.postMessage(
        { message: IFRAME_ACTIONS.IFRAME_HEIGHT, value: { height: 0 } },
        "*"
      );
    };
  }, [activeBodyKey, fixedViewport]);

  // Tag the document in fixed-viewport (panel) mode so the customizer cards can
  // compact themselves to a consistent height (see EnamorInlineCustomize.scss).
  useEffect(() => {
    if (!fixedViewport) return undefined;
    document.body.classList.add("dopplr-fixed-viewport");
    return () => document.body.classList.remove("dopplr-fixed-viewport");
  }, [fixedViewport]);

  // Phone panel (bottom sheet) vs desktop drawer, decided deterministically from
  // the iframe's own measured width — the desktop drawer iframe is a fixed ~496px,
  // phones are narrower. On phones the layout goes "bottom up": cards pinned at
  // the bottom (natural height), turntable fills everything above (see the
  // body.dopplr-panel-mobile rules in ModelViewerContainer.scss). Desktop keeps
  // its existing top-slice layout, untouched.
  useEffect(() => {
    if (!fixedViewport) return undefined;
    // EXACT source of truth: a cross-origin host (Shopify) can't be measured via
    // window.parent.innerWidth, so it POSTS the panel breakpoint decision
    // ({ message: "DOPPLR_VIEWPORT", value: { mobile } }, at the panel's own
    // 768px drawer-vs-sheet breakpoint). Prefer that flag whenever present.
    // Fall back to reading the parent width (same-origin jockeydemo) or the
    // iframe's own width (cross-origin, before the first flag arrives).
    let hostMobile = null;
    const heuristic = () => {
      try {
        return window.parent.innerWidth < 768;
      } catch (e) {
        return window.innerWidth < 460;
      }
    };
    const apply = () =>
      {
        document.body.classList.toggle(
          "dopplr-panel-mobile",
          hostMobile != null ? hostMobile : heuristic()
        );
        // A DESKTOP drawer can still be narrow: its width is aspect-locked to
        // the window height (min(100vh * 520/900, ...)), so a 768px-tall laptop
        // gets a ~419px panel, the card content wraps, its natural height grows
        // ~35%, and the fit-to-box scale collapses — 8.5px labels. This flag
        // applies the COMPACT metrics at narrow widths without switching to the
        // phone's bottom-up layout, which stays keyed on the mobile flag.
        document.body.classList.toggle(
          "dopplr-panel-narrow",
          window.innerWidth < 470
        );
      }
    const onMsg = (e) => {
      const m = e && e.data;
      if (
        m &&
        m.message === "DOPPLR_VIEWPORT" &&
        m.value &&
        typeof m.value.mobile === "boolean"
      ) {
        hostMobile = m.value.mobile;
        apply();
      }
    };
    apply();
    window.addEventListener("message", onMsg);
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    let parentWin = null;
    try { parentWin = window.parent; parentWin.addEventListener("resize", apply); } catch (e) {}
    return () => {
      window.removeEventListener("message", onMsg);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      try { parentWin && parentWin.removeEventListener("resize", apply); } catch (e) {}
      document.body.classList.remove("dopplr-panel-mobile");
      document.body.classList.remove("dopplr-panel-narrow");
    };
  }, [fixedViewport]);

  const [scale, setScale] = useState(
    avatarAssets && !isEmpty(avatarAssets.scalingFactor)
      ? avatarAssets.scalingFactor
      : { x: 1.0, y: 1.0, z: 1.0 }
  );
  let CAMERA_MODE =
    CAMERA_MODE_DEFAULT[selectedProduct.brand] ?? CAMERA_MODE_DEFAULT;

  const resetCamera = useCallback(() => {
    const cameraType = isShoeProduct(selectedProduct)
      ? CAMERA_TYPE.SHOES
      : CAMERA_TYPE.BODY;
    let next = { ...CAMERA_MODE[cameraType] };
    // /jockeydemo/1842/inline: keep the pulled-back framing on tap-outside reset,
    // and honor the open-card variant if a sizing card is currently open.
    if (isJockey1842Inline()) next = applyJockey1842Overrides(next);
    setCameraMode(next);
  }, [CAMERA_MODE, setCameraMode, selectedProduct]);

  useEffect(() => {
    const createHotspots = (product) => {
      const hotspots = (product.hotspots ?? []).map((hotspot) => {
        const {
          userAvatar: { bustShape, bustPosition },
        } = isDefaultAvatar ? defaultUserProfile : userProfile;
        let key =
          bustShape && bustPosition ? `${bustShape}_${bustPosition}` : "";
        let { id } = hotspot;
        const {
          normal = "",
          modelOrbit,
          modelPosition,
        } = hotspot[key] || hotspot["default"] || {};
        const updatedHotspot = {
          slot: "hotspot-" + id,
          annotation: hotspot.messages?.[0] ?? "",
          position: modelPosition,
          normal,
          orbit: modelOrbit,
        };
        return updatedHotspot;
      });
      return hotspots;
    };
    if (!isEmpty(avatarAssets)) {
      // avatarUrl / asset URLs can be briefly absent while the avatar assets are
      // still resolving (e.g. a product-config API 401 in a local/no-session
      // run). Guard the splits so a half-populated avatarAssets doesn't crash the
      // whole experience mount — the turntable path renders once its body is ready.
      const _clean = (u) => (typeof u === "string" ? u.split("?")[0] : "");
      if (isShoeProduct(selectedProduct)) {
        const src = _clean(avatarAssets.assets && avatarAssets.assets["shoeUrl"]);
        setSelectedScene({
          src,
          additionalAssets: null,
          hotspots: [],
        });
      } else {
        const showNewBody = selectedProduct.brand === "fef";
        const src = showNewBody
          ? "https://assets.dopplr.info/female_VS.glb"
          : _clean(avatarAssets.avatarUrl);
        const additionalAssets = JSON.stringify(
          Object.values(showNewBody ? [] : (avatarAssets.assets || {}))
            .filter((asset) => typeof asset === "string" && asset)
            .map((asset) => ({
              url: asset.split("?")[0],
              position: { x: 0, y: 0, z: 0 },
            }))
        );
        setSceneLoading(true);
        const hotspots = createHotspots(selectedProduct);
        const cameraType = isShoeProduct(selectedProduct)
          ? CAMERA_TYPE.SHOES
          : CAMERA_TYPE.BODY;
        {
          let mode = CAMERA_MODE[cameraType];
          if (isJockey1842Inline()) mode = applyJockey1842Overrides(mode);
          setCameraMode(mode);
        }
        const newSceneData = {
          src,
          additionalAssets,
          hotspots,
        };

        // Update selectedScene; model-viewer reloads natively on src change.
        // (We previously force-remounted via key={src}, but that tore down
        // the shared Three.js renderer in model-viewer's disconnectedCallback,
        // leaving the next instance unable to render — manifested as a blank
        // canvas after Bell + tone change on iOS / mobile emulation.)
        setSelectedScene(newSceneData);
      }
      // console.log(sources);
    }
  }, [
    defaultUserProfile,
    isDefaultAvatar,
    selectedProduct,
    userProfile,
    avatarAssets,
    setCameraMode,
    CAMERA_MODE,
  ]);

  const showScaling = false;
  // typeof window !== "undefined" &&
  // (window.location.origin === "https://qa.dopplr.info" ||
  //   window.location.origin === "http://localhost:3000") &&
  // typeof window !== "undefined" &&
  // window.location.pathname === "/dopplr-plugin/enamor";

  const handleScale = (e, key) => {
    const value = parseFloat(e?.target?.value ?? 1) || 1;
    setScale((prev) => ({ ...prev, [key]: value }));
  };

  const openScaling = async () => {
    setIsScalingActive((prev) => !prev);
  };

  const closeScaling = async () => {
    setIsScalingActive((prev) => !prev);
  };

  const getModelViewerConfigProps = () => {
    let props;
    if (selectedProduct?.modelViewerConfig) {
      const {
        exposure = 1,
        environmentImage,
        skyboxImage,
        toneMapping = "aces",
      } = selectedProduct.modelViewerConfig;
      props = {
        exposure,
        environmentImage: environmentImage
          ? environmentImage
          : getDefaultEnvironment(selectedProduct, selectedSkybox),
        skyboxImage: skyboxImage
          ? skyboxImage
          : getDefaultSkybox(selectedProduct, selectedSkybox),
        toneMapping,
      };
    } else {
      props = {
        exposure: "1",
        environmentImage: getDefaultEnvironment(selectedProduct, selectedSkybox),
        skyboxImage: getDefaultSkybox(selectedProduct, selectedSkybox),
        toneMapping: "aces",
      };
    }
    // /jockeydemo/*/inline: baseline brighter exposure + white IBL so the
    // avatar reads cleanly on the white stage. lightTuning (Controls-panel
    // sliders) still overrides when present. 1820 is the exception —
    // it uses the POC-picker stage (pillars HDR + exposure 1.0).
    if (isJockey1820Inline()) {
      props.exposure = "1.0";
      props.toneMapping = "neutral";
    } else if (isJockey1842Inline()) {
      props.exposure = "3.0";
      props.toneMapping = "neutral";
    }
    if (isJockey1842Inline() && !isJockey1820Inline() && lightTuning) {
      props.exposure = String(lightTuning.brightness ?? 3.0);
      if (typeof lightTuning.shadow === "number") {
        props.shadowIntensity = String(lightTuning.shadow);
      }
      if (typeof lightTuning.toneMapping === "string") {
        props.toneMapping = lightTuning.toneMapping;
      }
    }
    return props;
  };

  if (!selectedScene) {
    return null;
  }

  // Contrast: CSS filter on the canvas — model-viewer has no continuous PBR
  // contrast attribute. Tinted overlays (warmth=orange, coolness=blue) sit on
  // top with mix-blend-mode so they shift hue without crushing values.
  const lt = isJockey1842Inline() && lightTuning ? lightTuning : null;
  const containerStyle = lt
    ? { filter: `contrast(${lt.contrast ?? 1})` }
    : undefined;
  const warmthAlpha = lt ? Math.max(0, Math.min(1, lt.warmth ?? 0)) * 0.35 : 0;
  const coolnessAlpha = lt ? Math.max(0, Math.min(1, lt.coolness ?? 0)) * 0.35 : 0;

  return (
    <div className="ModelViewer__Container" style={containerStyle}>
      {(() => {
        const renderBody = renderBodyFromAvatar(avatarAssets);
        // Cup+band for the turntable body, in priority order:
        //   1) turntableSizeSel — the shopper's explicit band+cup pick (set by
        //      the size picker). This is the ONLY signal that varies by cup AND
        //      band for sports bras (216/217), whose clothSize is alpha "M".
        //   2) clothSize — the server-predicted band+cup (regular bras).
        //   3) the scanned avatar body's own cup+band.
        // Shape always comes from the avatar. Only B/C/D cups + bands 34/40 are
        // rendered (other bands x-scale), so non-rendered cups fall through.
        let turntableSize = null;
        // `body` can be briefly absent while the avatar assets are still
        // resolving (e.g. product-config API 401 in a local/no-session run) —
        // guard the split so a half-populated renderBody doesn't crash the mount.
        const renderBodyReady = renderBody && typeof renderBody.body === "string" && renderBody.body.includes("_");
        if (renderBodyReady) {
          const avatarSize = renderBody.body.split("_")[1]; // e.g. "C34"
          const RENDERED_CUPS = ["B", "C", "D"];
          const sel = /^([A-Z]+)(\d{2})$/.exec(turntableSizeSel || "");
          const cloth = sizeRecommendations?.clothSize || "";
          const clothCup = (cloth.match(/[A-Za-z]+/) || [])[0]?.toUpperCase();
          const clothBand = (/(\d{2})/.exec(cloth) || [])[1];
          const cup =
            sel && RENDERED_CUPS.includes(sel[1]) ? sel[1]
            : RENDERED_CUPS.includes(clothCup) ? clothCup
            : avatarSize.charAt(0);
          const band = (sel && sel[2]) || clothBand || avatarSize.slice(1);
          turntableSize = `${cup}${band}`;
        }
        return renderBodyReady ? (
          <RenderTurntable
            product={renderBody.product}
            // Live shape override from the "Your Bust" firmness/projection step
            // (SET_TURNTABLE_SHAPE) takes precedence over the avatar-GLB-derived
            // shape, so the bust swaps the moment a shape is picked — before the
            // PREVIEW-time avatar rebuild. Falls back to the avatar's own shape.
            shape={turntableShapeSel || renderBody.body.split("_")[0]}
            size={turntableSize}
            sizeLabel={braSizeLabel}
            // "Model size" until the shopper confirms their own measurements/size
            // (which dispatches SET_TURNTABLE_SIZE → turntableSizeSel), then "Your size".
            measured={turntableSizeSel != null}
            flow={!fixedViewport}
            onReady={() => setSceneLoading(false)}
          />
        ) : (
          <ModelViewer
            config={selectedScene || {}}
            setLoading={setSceneLoading}
            loading={sceneLoading}
            showHotspots={showHotspots}
            selectedProduct={selectedProduct}
            scale={scale}
            selectedSkybox={selectedSkybox}
            // cameraTarget={
            //   isShoeProduct(selectedProduct) ? "0.01m 0.05m 0m" : "0.01m 1.25m 0m"
            // }
            cameraPosition={getCameraPosition(cameraMode)}
            exposure={isShoeProduct(selectedProduct) ? "0.9" : "1"}
            resetCamera={resetCamera}
            {...getModelViewerConfigProps()}
          />
        );
      })()}
      {lt && (warmthAlpha > 0 || coolnessAlpha > 0) && (
        <div
          className="ModelViewer__TintOverlay"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            mixBlendMode: "overlay",
            background: `linear-gradient(
              rgba(255, 170, 90, ${warmthAlpha}),
              rgba(255, 170, 90, ${warmthAlpha})
            ), linear-gradient(
              rgba(90, 170, 255, ${coolnessAlpha}),
              rgba(90, 170, 255, ${coolnessAlpha})
            )`,
          }}
        />
      )}
      {showScaling && (
        <div className="DopplrScene__Scale">
          {isScalingActive ? (
            <>
              <TextField
                label={"X"}
                size="small"
                className="Measurements__TextField"
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    marginRight: "1em",
                    marginBottom: "0.5em",
                    borderRadius: "1em",
                  },
                  ".MuiFormHelperText-root.Mui-error": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
                type="number"
                inputProps={{
                  step: "0.01",
                }}
                value={scale.x || "1.00"}
                onChange={(e) => handleScale(e, "x")}
              />
              <TextField
                label={"Y"}
                size="small"
                step="0.01"
                className="Measurements__TextField"
                inputProps={{
                  step: "0.01",
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    marginRight: "1em",
                    marginBottom: "0.5em",
                    borderRadius: "1em",
                  },
                  ".MuiFormHelperText-root.Mui-error": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
                type="number"
                value={scale.y || "1.00"}
                onChange={(e) => handleScale(e, "y")}
              />
              <TextField
                label={"Z"}
                size="small"
                step="0.01"
                className="Measurements__TextField"
                inputProps={{
                  step: "0.01",
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    marginRight: "1em",
                    marginBottom: "0.5em",
                    borderRadius: "1em",
                  },
                  ".MuiFormHelperText-root.Mui-error": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
                type="number"
                value={scale.z || "1.00"}
                onChange={(e) => handleScale(e, "z")}
              />
              <Button onClick={closeScaling}>Scale</Button>
            </>
          ) : (
            <Button onClick={openScaling}>Scale</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelViewerContainer;
